import { useMutation } from "@tanstack/react-query";
import { EllipsisVerticalIcon, LogOutIcon } from "lucide-react";
import { useNavigate } from "react-router";
import { signOut } from "@/api/auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";

function initials(label: string): string {
  return label.slice(0, 2).toUpperCase();
}

/** Footer avatar dropdown — sign-out only; GAS has no account/billing/notification settings to add here. */
export function NavUser({ label, description, email }: { label: string; description?: string; email: string }) {
  const navigate = useNavigate();
  const { isMobile } = useSidebar();
  const signOutMutation = useMutation({
    mutationFn: signOut,
    meta: { errorTitle: "Couldn't sign out" },
    onSuccess: () => void navigate("/sign-in", { replace: true }),
  });

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                size="lg"
                className="data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">{initials(label)}</AvatarFallback>
                </Avatar>
                <span className="flex flex-1 flex-col truncate text-left leading-tight">
                  <span className="truncate text-sm font-medium">{label}</span>
                  {description ? <span className="truncate text-xs text-muted-foreground">{description}</span> : null}
                </span>
                <EllipsisVerticalIcon className="ml-auto size-4" />
              </SidebarMenuButton>
            }
          />
          <DropdownMenuContent
            className="w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="font-normal">
              <span className="block truncate">{label}</span>
              {description ? <span className="block truncate text-xs text-muted-foreground">{description}</span> : null}
              <span className="block truncate text-xs text-muted-foreground">{email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => signOutMutation.mutate()}
              disabled={signOutMutation.isPending}
              variant="destructive"
            >
              <LogOutIcon />
              {signOutMutation.isPending ? "Signing out…" : "Sign out"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
